import { Component, type ErrorInfo, type ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/core";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// React only catches render/lifecycle errors via a class component's
// getDerivedStateFromError/componentDidCatch — there's no hook equivalent.
// Without this, any uncaught error anywhere under it unmounts the whole
// React tree and blanks the page, which is what was happening (e.g. reported
// during column resizing) with no boundary in place at all.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Uncaught render error", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-background px-6 text-center">
          <div>
            <h1 className="text-lg font-bold text-foreground">Something went wrong</h1>
            <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">
              This part of the page hit an unexpected error. Reloading usually fixes it.
            </p>
          </div>
          <Button onClick={() => { window.location.reload(); }}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
